import { LitElement, css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { Artifact } from '../artifact.js';
import { linkStyles } from './cssstyles.js';


/**
 * ImageDiff must have a "diff" and some 2 other expectations, such as "actual", "expected" or "previous".
 * At least that's what Playwright Test gives us.
 */
export type ImageExpectation = Artifact & {
  category: string,
};
export type ImageDiff = {
  name: string,
  diff?: Artifact,
  expectation1: ImageExpectation;
  expectation2: ImageExpectation;
}

@customElement('image-diff-attachment')
class ImageDiffAttachment extends LitElement {

  @property() diff?: ImageDiff;
  render() {
    const imageDiff = this.diff;
    if (!imageDiff)
      return nothing;
    return html`
        <fk-tabbar>
          ${imageDiff.diff ? html`
            <fk-tab name="diff">Diff</fk-tab>
            <fk-tabpane name="diff">
              <img class="screenshot-diff dropshadow" src=${imageDiff.diff.url}>
            </fk-tabpane>
          ` : nothing}

          <fk-tab name="expectation-1">${capitalize(imageDiff.expectation1.category)}</fk-tab>
          <fk-tab name="expectation-2">${capitalize(imageDiff.expectation2.category)}</fk-tab>
          <fk-tab name="slider">Slider</fk-tab>

          <fk-tabpane name="expectation-1">
            <img class="screenshot-diff dropshadow"
              alt=${imageDiff.expectation1.category}
              src=${imageDiff.expectation1.url}
            >
          </fk-tabpane>
          <fk-tabpane name="expectation-2">
            <img class="screenshot-diff dropshadow"
              alt=${imageDiff.expectation2.category}
              src=${imageDiff.expectation2.url}
            >
          </fk-tabpane>
            <fk-tabpane name="slider">
              <sl-image-comparer class="screenshot-diff dropshadow">
                <img
                  slot="before"
                  alt=${imageDiff.expectation1.category}
                  src=${imageDiff.expectation1.url}
                />
                <img
                  slot="after"
                  alt=${imageDiff.expectation2.category}
                  src=${imageDiff.expectation2.url}
                />
            </sl-image-comparer>
          </fk-tabpane>
        </fk-tabbar>
        <v-box class=captions>
          ${imageDiff.diff ? this._renderCaption(imageDiff.diff) : nothing}
          ${this._renderCaption(imageDiff.expectation1)}
          ${this._renderCaption(imageDiff.expectation2)}
        </v-box>
    `
  }

  private _renderCaption(artifact: Artifact) {
    return html`
      <h-box class=caption>
        <span class=filename>${artifact.fileName}</span>
        <a class=download href=${artifact.url} download=${artifact.fileName}>
          <sl-icon name=download></sl-icon>
        </a>
      </h-box>
    `;
  }

  static styles = [linkStyles, css`
    .screenshot-diff {
      margin: var(--sl-spacing-small);
      max-width: var(--image-diff-max-width, 800px);
      border-radius: var(--sl-border-radius-large);

      img {
        max-width: 100%;
      }
    }

    .dropshadow {
      box-shadow: 0 0 4px 0px var(--sl-color-neutral-400);
    }

    sl-image-comparer::part(divider) {
      border: 1px solid var(--sl-color-neutral-400);
    }

    sl-image-comparer::part(handle) {
      border: 2px solid var(--sl-color-neutral-400);
    }

    .captions {
      margin: var(--sl-spacing-small);
      gap: var(--sl-spacing-2x-small);
    }

    .caption {
      gap: var(--sl-spacing-2x-small);
      align-items: center;
      font-size: var(--sl-font-size-small);
    }

    .filename {
      font-family: var(--sl-font-mono);
    }

    .download {
      display: inline-flex;
      align-items: center;
      color: var(--sl-color-neutral-600);
    }

    .download:hover {
      color: var(--sl-color-primary-600);
    }
  `];
}

function capitalize(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}
